import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { Alert, Button, Col, Form, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import EventBus from "../../../common/EventBus"
import ConfirmButton from "../../../common/button/confirmbutton.component"
import LabelInputFormGroup from "../../../common/form/LabelInputFormGroup"
import LabelRichTextFormGroup from "../../../common/form/LabelRichTextFormGroup"
import LabelSelectFormGroup from "../../../common/form/LabelSelectFormGroup"
import ImageUploaderDropZone from "../../../common/image-uploader/dropzone.component"
import { BundleFormAttributes, BundleFormProps } from "../../../common/types/bundle"
import IntlProps from "../../../state/helpers/IntlProps"
import { TYPE_TAG } from "../../../state/label/labelTypes"
import { updateProduct } from "../../../state/product/ProductActions"
import { getStore } from "../../../state/product/ProductSelectors"
import { BILLING_METHOD, PRICING_STRATEGY, PRODUCT_TYPE, isProductUpdateOption } from "../../../state/product/Types"
import { handleTotalAmount } from "../../../utils/bundle"
import { messages } from "../../../utils/bundle/constants"
import BundleCompositionForm from "./BundleCompositionForm"

const BundleForm = (props: BundleFormProps & IntlProps) => {
    const dispatch = useDispatch()
    const bundleProduct = useSelector(state => getStore(state))
    const [errors, setErrors] = useState({ title: "", bundle: "" })
    const [showAlert, setShowAlert] = useState(true)
    const [form, setForm] = useState<BundleFormAttributes>({
        title: bundleProduct?.title || "",
        shortDescription: bundleProduct?.shortDescription || "",
        longDescription: bundleProduct?.longDescription || "",
        sku: bundleProduct?.sku || "",
        barcode: bundleProduct?.barcode || "",
        currency: AppContext.getAccount().attributes.currency || bundleProduct?.currency,
        billingMethods: bundleProduct?.billingMethods || [BILLING_METHOD.ONE_TIME],
        labelIds: bundleProduct?.labels.map(label => label.id) || [],
        imageIds: bundleProduct?.imageIds || [],
        images: bundleProduct?.images || [],
        isNonInventory: false,
        taxExempt: false,
        type: PRODUCT_TYPE.BUNDLE,
        bracketSize: "1",
        measureMinimum: "0",
        pricingStrategy: PRICING_STRATEGY.MULTIPLIER,
        defaultUnitPrice: bundleProduct?.defaultUnitPrice || "0.00",
        bundle: bundleProduct.bundle?.attributes || [],
    })

    useEffect(() => {
        setShowAlert(true)
        const timeId = setTimeout(() => {
            setShowAlert(false)
        }, 3000)

        return () => {
            clearTimeout(timeId)
        }
    }, [props.error])

    useEffect(() => {
        setForm(prevForm => ({
            ...prevForm,
            title: bundleProduct?.title || "",
            shortDescription: bundleProduct?.shortDescription || "",
            longDescription: bundleProduct?.longDescription || "",
            sku: bundleProduct?.sku || "",
            barcode: bundleProduct?.barcode || "",
            labelIds: bundleProduct?.labelIds || bundleProduct?.labels.map(label => label.id) || [],
            defaultUnitPrice: handleTotalAmount(bundleProduct?.bundle?.attributes.bundleProducts || []).toString(),
            bundle: bundleProduct?.bundle?.attributes
        }))
    }, [bundleProduct])

    useEffect(() => {
        EventBus.dispatch(EventBus.TopicUploaderImagesLoaded, {
            images: bundleProduct.images
        })
        setForm(prevForm => ({
            ...prevForm,
            imageIds: bundleProduct?.imageIds,
            images: bundleProduct?.images
        }))
    }, [bundleProduct?.images.length])

    const findFormErrors = () => {
        const { title, bundle } = form
        const newErrors = { title: "", bundle: "" }

        if (!title || title === "" || title.trim().length === 0) {
            newErrors.title = props.intl.formatMessage(messages["product.bundle.forms.bundle_form.title_blank_error"])
        }
        if(!bundle || bundle.bundleProducts?.length === 0) {
            newErrors.bundle = props.intl.formatMessage(messages["product.bundle.forms.bundle_form.empty_bundle_error"])
        }
        return newErrors
    }

    const handleSave = () => {
        const newErrors = findFormErrors()

        setErrors({
            title: newErrors.title,
            bundle: newErrors.bundle
        })

        const hasErrors = Object.values(newErrors).some(errorMsg => errorMsg !== "")

        if (!hasErrors) {
            props.upsertCallback(form)
        }
    }

    const deleteProduct = () => {
        props.deleteCallback()
    }

    const handleImageChange = (images) => {
        if(images.length > 0) {
            setForm(prevForm => ({
                ...prevForm,
                imageIds: images.map(i => i.id),
                images: images
            }))
        } else {
            setForm(prevForm => ({
                ...prevForm,
                imageIds: [],
                images: []
            }))
        }
    }
    

    const setFormField = (field: string, value: string) => {
        setForm(prevForm => ({
            ...prevForm,
            [field]: value
        }))

        if (isProductUpdateOption(field)) {
            dispatch(updateProduct({ updateType: field, value: value }))
        }  

        if (errors[field]) {
            setErrors({
                ...errors,
                [field]: null
            })
        }
    }

    const isSaveButtonDisabled = !form.title
    return (
        <>
            {props.error  && showAlert &&
                <Alert variant="danger" onClose={() => setShowAlert(false)} dismissible>
                    {props.error.message}
                </Alert>
            }
            <Form className="ui-has-modules">
                <Row>
                    <Col md={8} sm={12}>
                        <Row className="px-3">
                            <Col className="ui-module">
                                <LabelInputFormGroup
                                    formLabel={
                                        <FormattedMessage id="product.bundle.forms.bundle_form.title" defaultMessage="Title" />
                                    }
                                    inputProps={{
                                        value: form.title,
                                        placeholder: props.intl.formatMessage(
                                            messages["product.bundle.forms.bundle_form.title_placeholder"]),
                                        id: "title",
                                        onChange: e => setFormField("title", e.target.value)
                                    }}
                                >
                                    <small><div className="text-danger">{errors.title}</div></small>
                                </LabelInputFormGroup>
                                
                                <LabelInputFormGroup
                                    formLabel={
                                        <FormattedMessage
                                            id="product.bundle.forms.bundle_form.short_description"
                                            defaultMessage="Short Description"
                                        />
                                    }
                                    inputProps={{
                                        value: form.shortDescription,
                                        placeholder: props.intl.formatMessage(
                                            messages["product.bundle.forms.bundle_form.short_description_placeholder"]
                                        ),
                                        id: "shortDescription",
                                        onChange: e => setFormField("shortDescription", e.target.value)
                                    }}
                                />
                                <LabelRichTextFormGroup
                                    formLabel={
                                        <FormattedMessage
                                            id="product.bundle.forms.bundle_form.long_description"
                                            defaultMessage="Long Description"
                                        />
                                    }
                                    richTextProps={{
                                        className: "form-control",
                                        enableMergeFields: false,
                                        simple: true,
                                        height: 300,
                                        name: "longDescription",
                                        id: "longDescription",
                                        value: form.longDescription,
                                        onChange: (items) => setFormField("longDescription", items)
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row className="px-3">
                            <Col className="ui-module">
                                <BundleCompositionForm errors={errors.bundle} />
                            </Col>
                        </Row>
                    </Col>
                    <Col md={4} sm={12}>
                        <Row className="px-3">
                            <Col className="ui-module">
                                <h4 className="p-0 custom-lead">
                                    <FormattedMessage
                                        id="product.bundle.forms.bundle_form.categorization"
                                        defaultMessage="Categorization"
                                    />
                                </h4>
                                <LabelInputFormGroup
                                    formLabel={
                                        <FormattedMessage id="product.bundle.forms.bundle_form.sku" defaultMessage="Sku" />
                                    }
                                    inputProps={{
                                        value: form.sku,
                                        placeholder: props.intl.formatMessage(
                                            messages["product.bundle.forms.bundle_form.sku_placeholder"]
                                        ),
                                        id: "sku",
                                        onChange: e => setFormField("sku", e.target.value)
                                    }}
                                />
                                <LabelInputFormGroup
                                    formLabel={
                                        <FormattedMessage
                                            id="product.bundle.forms.bundle_form.barcode"
                                            defaultMessage="Barcode"
                                        />
                                    }
                                    inputProps={{
                                        value: form.barcode,
                                        placeholder: props.intl.formatMessage(
                                            messages["product.bundle.forms.bundle_form.barcode_placeholder"]
                                        ),
                                        id: "barcode",
                                        onChange: e => setFormField("barcode", e.target.value)
                                    }}
                                />
                                <LabelSelectFormGroup
                                    formLabel={
                                        <FormattedMessage id="product.bundle.forms.bundle_form.labels" defaultMessage="Labels" />
                                    }
                                    labelSelectProps={{
                                        placeholder: props.intl.formatMessage(
                                            messages["product.bundle.forms.bundle_form.labels_placeholder"]
                                        ),
                                        value: form.labelIds,
                                        onChange: (items) => setFormField("labelIds", items),
                                        multi: true,
                                        creatable: true,
                                        id: "labelIds",
                                        type: TYPE_TAG
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row className="px-3">
                            <Col className="ui-module">
                                <h4 className="p-0 custom-lead">
                                    <FormattedMessage
                                        id="product.bundle.forms.bundle_form.images"
                                        defaultMessage="Images"
                                    />
                                </h4>
                                <ImageUploaderDropZone
                                    onUpdateImageList={handleImageChange}
                                    maxFiles={5}
                                    images={form.images}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row style={{zIndex:1500} }  className="m-0 py-1 fixed-bottom pr-0 pr-md-5 pr-xl-0">
                    <Col lg={12} xl={11} className="mx-auto pt-3 pl-4 pr-4 pr-md-5 pr-xl-4">
                        <Col className="ui-module sticky-bottom-module">
                            <Row>
                                <Col xs={5} md={6}>
                                    { <ConfirmButton
                                        onClick={deleteProduct}
                                        className="btn btn-danger"
                                    >
                                        <FormattedMessage
                                            id="product.bundle.forms.bundle_form.delete_btn"
                                            defaultMessage="Delete"
                                        />
                                    </ConfirmButton>}
                                </Col>
                                <Col xs={7} md={6} className="text-align-right">
                                    <Link to="/bundles" className="btn btn-light mr-2">
                                        <FormattedMessage id="user.form.edit.cancel_btn" defaultMessage="Cancel" />
                                    </Link>
                                    <Button
                                        variant="primary"
                                        onClick={handleSave}
                                        disabled={isSaveButtonDisabled}
                                    >
                                        {props.intl.formatMessage(messages["product.bundle.forms.bundle_form.save_btn"])}
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                    </Col>
                </Row>
            </Form>
        </>
        
    )
}

export default injectIntl(BundleForm)


// WEBPACK FOOTER //
// ./src/product/bundle/forms/BundleForm.tsx